// components/booking/BookingTimeStep.tsx
import React, { useState, useEffect } from 'react';
import { ModernButton } from '../ui/ModernButton.tsx';
import { ChevronLeftIcon } from '../icons/index.tsx';

interface BookingTimeStepProps {
  selectedDate: string;
  selectedTime: string;
  userTimezone: string;
  onTimeSelect: (time: string, timezone: string) => void;
  onBack: () => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
}

interface TimeSlot {
  time: string;
  displayTime: string;
  available: boolean;
  period: 'morning' | 'midday' | 'afternoon' | 'evening';
}

export const BookingTimeStep: React.FC<BookingTimeStepProps> = ({
  selectedDate,
  selectedTime,
  userTimezone,
  onTimeSelect,
  onBack,
  setError,
  setLoading
}) => {
  const [currentTimezone, setCurrentTimezone] = useState(userTimezone);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);

  // Common timezones for the region
  const commonTimezones = [
    { value: 'Asia/Kuwait', label: 'الكويت (GMT+3)', flag: '🇰🇼' },
    { value: 'Asia/Beirut', label: 'بيروت (GMT+2/3)', flag: '🇱🇧' },
    { value: 'Asia/Dubai', label: 'دبي (GMT+4)', flag: '🇦🇪' },
    { value: 'Asia/Riyadh', label: 'الرياض (GMT+3)', flag: '🇸🇦' },
    { value: 'Africa/Cairo', label: 'القاهرة (GMT+2)', flag: '🇪🇬' },
    { value: 'Europe/Istanbul', label: 'إسطنبول (GMT+3)', flag: '🇹🇷' },
    { value: 'Europe/London', label: 'لندن (GMT+0/1)', flag: '🇬🇧' },
    { value: 'Europe/Paris', label: 'باريس (GMT+1/2)', flag: '🇫🇷' },
  ];

  useEffect(() => {
    generateTimeSlots();
  }, [selectedDate, currentTimezone]);

  const generateTimeSlots = () => {
    const slots: TimeSlot[] = [];
    
    // Generate slots from 8:00 AM to 10:00 PM (Kuwait time)
    for (let hour = 8; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        
        // Determine period
        let period: TimeSlot['period'];
        if (hour >= 8 && hour < 11) period = 'morning';
        else if (hour >= 11 && hour < 14) period = 'midday';
        else if (hour >= 14 && hour < 18) period = 'afternoon';
        else period = 'evening';

        // Format display time
        const displayTime = formatTime(timeString);
        
        slots.push({
          time: timeString,
          displayTime,
          available: true, // In a real app, you'd check availability
          period
        });
      }
    }
    
    setTimeSlots(slots);
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'م' : 'ص';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formatSelectedDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString('ar-JO', options);
  };

  const handleTimeSlotClick = (slot: TimeSlot) => {
    if (!slot.available) return;
    onTimeSelect(slot.time, currentTimezone);
  };

  const getSlotsByPeriod = (period: TimeSlot['period']) => {
    return timeSlots.filter(slot => slot.period === period);
  };

  const getPeriodTitle = (period: TimeSlot['period']) => {
    switch (period) {
      case 'morning': return 'الصباح';
      case 'midday': return 'الظهيرة';
      case 'afternoon': return 'بعد الظهر';
      case 'evening': return 'المساء';
    }
  };

  const getPeriodIcon = (period: TimeSlot['period']) => {
    switch (period) {
      case 'morning': return '🌅';
      case 'midday': return '☀️';
      case 'afternoon': return '🌤️';
      case 'evening': return '🌙';
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">اختر وقت الجلسة</h2>
          <p className="text-gray-600">الأوقات المتاحة ليوم {formatSelectedDate(selectedDate)}</p>
        </div>
        <ModernButton 
          variant="outline" 
          size="sm"
          onClick={onBack}
          icon={<ChevronLeftIcon />}
        >
          رجوع
        </ModernButton>
      </div>

      {/* Timezone Selector */}
      <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
        <div className="flex items-center mb-3">
          <div className="text-blue-600 ml-2">🌍</div>
          <span className="font-medium text-gray-900">المنطقة الزمنية:</span>
        </div>
        <select
          value={currentTimezone}
          onChange={(e) => setCurrentTimezone(e.target.value)}
          className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
        >
          {commonTimezones.map(tz => (
            <option key={tz.value} value={tz.value}>
              {tz.flag} {tz.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-2">
          الأوقات معروضة بالتوقيت المحلي للكويت. سيتم تحويلها حسب منطقتك الزمنية.
        </p>
      </div>

      {/* Selected time display */}
      {selectedTime && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="text-green-600 ml-3">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="text-green-800 font-medium">الوقت المحدد:</p>
              <p className="text-green-700">{formatTime(selectedTime)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Time Slots */}
      <div className="space-y-6">
        {(['morning', 'midday', 'afternoon', 'evening'] as const).map(period => {
          const periodSlots = getSlotsByPeriod(period);
          if (periodSlots.length === 0) return null;

          return (
            <div key={period} className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <span className="ml-2">{getPeriodIcon(period)}</span>
                {getPeriodTitle(period)}
                <span className="mr-2 text-sm text-gray-500">
                  ({periodSlots.length} وقت متاح)
                </span>
              </h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {periodSlots.map(slot => (
                  <button
                    key={slot.time}
                    onClick={() => handleTimeSlotClick(slot)}
                    disabled={!slot.available}
                    className={`
                      p-3 rounded-lg text-center transition-all duration-200 font-medium
                      ${selectedTime === slot.time
                        ? 'bg-blue-600 text-white ring-2 ring-blue-300 shadow-lg transform scale-105'
                        : slot.available
                          ? 'bg-white border-2 border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 hover:shadow-md'
                          : 'bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed'
                      }
                    `}
                  >
                    <div className="text-sm">{slot.displayTime}</div>
                    {!slot.available && (
                      <div className="text-xs mt-1 text-red-500">محجوز</div>
                    )}
                    {selectedTime === slot.time && (
                      <div className="text-xs mt-1">محدد ✓</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex gap-4">
        <ModernButton
          variant="outline"
          onClick={onBack}
          className="flex-1"
          icon={<ChevronLeftIcon />}
        >
          رجوع
        </ModernButton>
        
        {selectedTime && (
          <ModernButton
            variant="primary"
            onClick={() => onTimeSelect(selectedTime, currentTimezone)}
            className="flex-1"
          >
            متابعة للتسجيل ←
          </ModernButton>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-6 text-center text-sm text-gray-500">
        <p>جميع الأوقات معروضة بالتوقيت المحلي للكويت</p>
        <p className="mt-1">يمكنك تغيير المنطقة الزمنية من الأعلى لمشاهدة الأوقات بتوقيتك المحلي</p>
      </div>
    </div>
  );
};